/**
 * Portfolio API fetch utility
 * Automatically includes X-Agent-Private-Key header for all portfolio endpoints
 */

/**
 * Get the agent private key for a portfolio from localStorage
 * @param {number} portfolioId - The portfolio ID
 * @returns {string|null} The agent private key or null if not found
 */
export const getAgentPrivateKeyFromStorage = (portfolioId) => {
  // Try testnet key first, then mainnet
  const testnetKey = `hyperliquid_agent_testnet_${portfolioId}`;
  const mainnetKey = `hyperliquid_agent_mainnet_${portfolioId}`;

  let agentPrivateKey = localStorage.getItem(testnetKey) || localStorage.getItem(mainnetKey);

  // Also try legacy format for backward compatibility
  if (!agentPrivateKey) {
    agentPrivateKey = localStorage.getItem(`agent_private_key_${portfolioId}`);
  }

  return agentPrivateKey;
};

/**
 * Fetch a portfolio endpoint with automatic X-Agent-Private-Key header
 * @param {string} url - The full URL or path (e.g., '/portfolio/43' or 'http://localhost:8000/portfolio/43')
 * @param {object} options - Standard fetch options
 * @param {number} portfolioId - The portfolio ID (extracted from URL if not provided)
 * @returns {Promise<Response>} The fetch response
 */
export const portfolioFetch = async (url, options = {}, portfolioId = null) => {
  const headers = new Headers(options.headers || {});
  const authHeader = headers.get('Authorization');

  // Drop placeholder auth values so startup races do not send `Bearer undefined`.
  if (!authHeader || /Bearer\s+(undefined|null)$/i.test(authHeader.trim())) {
    headers.delete('Authorization');
  }

  // Extract portfolio ID from URL if not provided
  if (!portfolioId) {
    const match = url.match(/\/portfolio\/(\d+)/);
    if (match) {
      portfolioId = parseInt(match[1], 10);
    }
  }

  // Get agent private key if we have a portfolio ID
  if (portfolioId) {
    const agentPrivateKey = getAgentPrivateKeyFromStorage(portfolioId);
    if (agentPrivateKey) {
      headers.set('X-Agent-Private-Key', agentPrivateKey);
    }
  }

  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * Like portfolioFetch but parses JSON response
 * @param {string} url - The full URL or path
 * @param {object} options - Standard fetch options
 * @param {number} portfolioId - The portfolio ID (extracted from URL if not provided)
 * @returns {Promise<object>} The parsed JSON response
 */
export const portfolioFetchJson = async (url, options = {}, portfolioId = null) => {
  const response = await portfolioFetch(url, options, portfolioId);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};
